import { useEffect, useRef } from "react";
import { X, SquarePen, Film } from "lucide-react";
import type { LucideIcon } from "lucide-react";

type CreateAction = {
  id: "post" | "episode";
  label: string;
  description: string;
  icon: LucideIcon;
};

const ACTIONS: CreateAction[] = [
  { id: "episode", label: "Episode", description: "Publish a video or audio podcast episode.", icon: Film },
  { id: "post", label: "Post", description: "Share a show update with your listeners.", icon: SquarePen },
];

interface CreateMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CreateMenu({ isOpen, onClose }: CreateMenuProps) {
  const firstActionRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    firstActionRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleAction = (id: CreateAction["id"]) => {
    onClose();
    switch (id) {
      case "post":
        window.dispatchEvent(new Event("openCompose"));
        return;
      case "episode":
        window.dispatchEvent(new Event("openUploadEpisode"));
        return;
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 scroll-modal-overlay animate-fade-in"
        onClick={onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Create"
        className="relative w-full max-w-[480px] bg-(--butter-bg-elevated) rounded-[var(--butter-radius-surface)] butter-shadow-panel animate-compose-in"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-(--scroll-border)">
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 flex items-center justify-center rounded-full text-(--scroll-text) hover:bg-(--scroll-bg-tertiary) transition-colors"
          >
            <X size={20} />
          </button>
          <span className="scroll-heading text-base">Create</span>
          <span className="w-8" />
        </div>

        <div className="p-3 space-y-1">
          {ACTIONS.map((action, i) => (
            <button
              key={action.id}
              ref={i === 0 ? firstActionRef : undefined}
              type="button"
              onClick={() => handleAction(action.id)}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left hover:bg-(--scroll-bg-tertiary) transition-colors"
            >
              <span className="w-10 h-10 rounded-full flex items-center justify-center bg-(--scroll-bg-tertiary) text-(--scroll-text) shrink-0">
                <action.icon size={20} />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold text-(--scroll-text)">{action.label}</span>
                <span className="block text-xs text-(--scroll-text-secondary)">{action.description}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
